import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Mail, Smartphone, Save, Loader2, Check, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

const PREFERENCE_CATEGORIES = [
  {
    key: 'lead_alerts',
    title: 'Lead Alerts',
    description: 'New junk car leads matching your criteria',
    icon: '🚗',
    roles: ['buyer', 'admin']
  },
  {
    key: 'offer_notifications',
    title: 'Offer Notifications',
    description: 'When someone submits or accepts an offer',
    icon: '💰',
    roles: ['buyer', 'seller', 'admin']
  },
  {
    key: 'vehicle_updates',
    title: 'Vehicle Updates',
    description: 'Status changes for your inventory vehicles',
    icon: '📦',
    roles: ['dealer', 'admin']
  },
  {
    key: 'subscription_billing',
    title: 'Subscription & Billing',
    description: 'Plan changes, payment confirmations, and receipts',
    icon: '💳',
    roles: ['dealer', 'buyer', 'seller', 'admin']
  },
  {
    key: 'system_announcements',
    title: 'System Announcements',
    description: 'Platform updates, maintenance, and new features',
    icon: '📢',
    roles: ['dealer', 'buyer', 'seller', 'admin']
  }
];

export default function NotificationSettings({ userRole = 'dealer' }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await api.get('/api/notification-preferences');
      setPrefs(data);
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const handleToggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      PREFERENCE_CATEGORIES.forEach(cat => {
        payload[`${cat.key}_email`] = prefs[`${cat.key}_email`] ?? true;
        payload[`${cat.key}_inapp`] = prefs[`${cat.key}_inapp`] ?? true;
        payload[`${cat.key}_sms`] = prefs[`${cat.key}_sms`] ?? false;
      });
      await api.put('/api/notification-preferences', payload);
      setSaved(true);
      toast.success('Notification preferences saved!');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAllEmail = (enabled) => {
    const updates = {};
    PREFERENCE_CATEGORIES.filter(c => c.roles.includes(userRole)).forEach(cat => {
      updates[`${cat.key}_email`] = enabled;
    });
    setPrefs(prev => ({ ...prev, ...updates }));
    setSaved(false);
  };

  const handleToggleAllInApp = (enabled) => {
    const updates = {};
    PREFERENCE_CATEGORIES.filter(c => c.roles.includes(userRole)).forEach(cat => {
      updates[`${cat.key}_inapp`] = enabled;
    });
    setPrefs(prev => ({ ...prev, ...updates }));
    setSaved(false);
  };

  const handleToggleAllSms = (enabled) => {
    const updates = {};
    PREFERENCE_CATEGORIES.filter(c => c.roles.includes(userRole)).forEach(cat => {
      updates[`${cat.key}_sms`] = enabled;
    });
    setPrefs(prev => ({ ...prev, ...updates }));
    setSaved(false);
  };

  const relevantCategories = PREFERENCE_CATEGORIES.filter(c => c.roles.includes(userRole));

  if (loading || !prefs) {
    return (
      <div className="max-w-2xl">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-gray-200 rounded-lg" />
            <div className="h-4 w-72 bg-gray-100 rounded" />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-50 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="notification-settings">
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center shadow-lg">
            <Bell className="w-7 h-7 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-xl">Notification Preferences</h3>
            <p className="text-gray-500">Choose how and when you want to be notified</p>
          </div>
        </div>

        {/* Column Headers */}
        <div className="flex items-center justify-end gap-6 pr-2 mt-8 mb-4">
          <button 
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-blue-600 transition-colors"
            onClick={() => handleToggleAllEmail(!relevantCategories.every(c => prefs[`${c.key}_email`]))}
            data-testid="toggle-all-email"
          >
            <Mail className="w-4 h-4" /> Email
          </button>
          <button 
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-blue-600 transition-colors"
            onClick={() => handleToggleAllInApp(!relevantCategories.every(c => prefs[`${c.key}_inapp`]))}
            data-testid="toggle-all-inapp"
          >
            <Smartphone className="w-4 h-4" /> In-App
          </button>
          <button 
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-green-600 transition-colors"
            onClick={() => handleToggleAllSms(!relevantCategories.every(c => prefs[`${c.key}_sms`]))}
            data-testid="toggle-all-sms"
          >
            <MessageSquare className="w-4 h-4" /> SMS
          </button>
        </div>

        {/* Preference Rows */}
        <div className="space-y-3">
          {relevantCategories.map((category) => (
            <div 
              key={category.key}
              className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100/80 transition-colors"
              data-testid={`pref-row-${category.key}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-2xl">{category.icon}</span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm">{category.title}</p>
                  <p className="text-xs text-gray-500 truncate">{category.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 shrink-0">
                <Switch
                  checked={prefs[`${category.key}_email`] ?? true}
                  onCheckedChange={() => handleToggle(`${category.key}_email`)}
                  data-testid={`toggle-${category.key}-email`}
                />
                <Switch
                  checked={prefs[`${category.key}_inapp`] ?? true}
                  onCheckedChange={() => handleToggle(`${category.key}_inapp`)}
                  data-testid={`toggle-${category.key}-inapp`}
                />
                <Switch
                  checked={prefs[`${category.key}_sms`] ?? false}
                  onCheckedChange={() => handleToggle(`${category.key}_sms`)}
                  data-testid={`toggle-${category.key}-sms`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-100">
          <p className="text-sm text-blue-700">
            <strong>Email:</strong> Receive notifications to your registered email.
            <br />
            <strong>In-App:</strong> Real-time alerts and badge counts in the dashboard.
            <br />
            <strong>SMS:</strong> Text message alerts to your phone number (requires Twilio setup).
          </p>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className={`rounded-xl px-8 h-12 shadow-lg transition-all ${
              saved 
                ? 'bg-green-500 hover:bg-green-600 text-white' 
                : 'bg-gradient-to-r from-indigo-500 to-blue-500 hover:opacity-90 text-white'
            }`}
            data-testid="save-preferences-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : saved ? (
              <><Check className="w-4 h-4 mr-2" /> Saved!</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Preferences</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
